/**
 * Kontrak "Log penggunaan penyedia AI" (v2/M5 = tahap log penggunaan). Dipakai
 * server (`GET /api/admin/ai-assistant/usage-log` dan `/usage-log/summary`) dan
 * tabel di sub menu admin "Pemakaian & Biaya".
 *
 * HANYA metadata: tidak pernah memuat isi percakapan (`content`) maupun
 * identitas pengguna — log ini untuk memantau penyedia, bukan untuk membaca
 * pertanyaan warga.
 */

/** Vendor yang menjawab satu giliran. `unknown` = pesan sebelum M5 atau nama model tak dikenal. */
export const USAGE_LOG_VENDORS = ["claude", "chatgpt", "unknown"] as const;
export type UsageLogVendor = (typeof USAGE_LOG_VENDORS)[number];

/** Status giliran yang masuk log: `ok` = terjawab, `error` = penyedia gagal menjawab. */
export const USAGE_LOG_STATUSES = ["ok", "error"] as const;
export type UsageLogStatus = (typeof USAGE_LOG_STATUSES)[number];

/** Paginasi wajib: `limit` bawaan dan batas atasnya. */
export const USAGE_LOG_DEFAULT_LIMIT = 50;
export const USAGE_LOG_MAX_LIMIT = 200;

/** Rentang tanggal (hari kalender WITA) yang boleh diminta; membatasi biaya query. */
export const USAGE_LOG_DEFAULT_DAYS = 7;
export const USAGE_LOG_MAX_DAYS = 90;

/**
 * Potongan nama model per vendor. Nama model adalah bukti paling jujur soal
 * siapa yang menjawab: slot hanya konvensi konfigurasi (slot `chat` memegang
 * profil Claude, `chat_backup` memegang profil OpenAI) dan admin bisa saja
 * mengisi slot `chat` dengan kredensial OpenAI.
 */
const CLAUDE_MODEL_HINTS = ["claude", "sonnet", "opus", "haiku"] as const;
const CHATGPT_MODEL_HINTS = [
	"gpt",
	"chatgpt",
	"o1-",
	"o3-",
	"o4-",
	"davinci",
] as const;

const matches = (model: string, hints: readonly string[]): boolean =>
	hints.some((hint) => model.includes(hint));

/**
 * Vendor satu pesan: nama model dulu (bukti nyata), lalu konvensi slot
 * (`chat` → Claude, `chat_backup` → ChatGPT), lalu `unknown`. Fungsi murni —
 * satu-satunya sumber label vendor di server maupun UI.
 */
export function vendorOfMessage(row: {
	slot?: string | null;
	model?: string | null;
}): UsageLogVendor {
	const model = row.model?.toLowerCase() ?? "";
	if (model && matches(model, CLAUDE_MODEL_HINTS)) return "claude";
	if (model && matches(model, CHATGPT_MODEL_HINTS)) return "chatgpt";
	if (row.slot === "chat") return "claude";
	if (row.slot === "chat_backup") return "chatgpt";
	return "unknown";
}

/** Satu baris log: metadata giliran, tanpa isi pesan dan tanpa pengguna. */
export interface UsageLogRowDto {
	id: string;
	/** Waktu giliran (ISO UTC; UI menampilkannya dalam WITA). */
	at: string;
	vendor: UsageLogVendor;
	/** Slot profil yang menjawab; null = tidak diketahui. */
	slot: string | null;
	/** Model yang menjawab; null = tidak diketahui. */
	model: string | null;
	status: UsageLogStatus;
	modality: "text" | "voice";
	latencyMs: number | null;
	inputTokens: number | null;
	outputTokens: number | null;
}

/** Respons `GET /api/admin/ai-assistant/usage-log`. */
export interface UsageLogPageDto {
	rows: UsageLogRowDto[];
	/** id baris terakhir; null = halaman terakhir. */
	nextCursor: string | null;
	rangeDays: number;
}

/** Ringkasan satu vendor pada rentang terpilih. */
export interface UsageLogVendorSummaryDto {
	vendor: UsageLogVendor;
	turns: number;
	errors: number;
	/** Porsi giliran vendor ini dari total rentang (0–100, satu desimal). */
	percent: number;
	inputTokens: number;
	outputTokens: number;
}

/** Respons `GET /api/admin/ai-assistant/usage-log/summary`. */
export interface UsageLogSummaryDto {
	rangeDays: number;
	/** Awal rentang (ISO) = 00:00 WITA hari pertama rentang. */
	since: string;
	totalTurns: number;
	totalErrors: number;
	/** Selalu ketiga vendor (urut Claude, ChatGPT, tidak diketahui) walau 0. */
	vendors: UsageLogVendorSummaryDto[];
}

/** Query kedua endpoint log (sudah divalidasi skema route). */
export interface UsageLogQuery {
	vendor?: UsageLogVendor;
	status?: UsageLogStatus;
	days?: number;
	limit?: number;
	cursor?: string;
}
